import type { ReactNode } from "react"

import type { BatteryArbitragePresentationDto } from "~/api/dto/battery-arbitrage.dto"

import {
  arbitrageBasisQualifier,
  arbitrageBillLabel,
  arbitrageScopeExplanation,
} from "../arbitrage-assumptions"
import { GradText, Panel, SectionHeading } from "../primitives"
import { formatUsd, reducesUtilityBill } from "../view-helpers"

type Arbitrage = BatteryArbitragePresentationDto

type ProjectionYear = {
  year: number
  doNothingUsd: number
  withBatteryUsd: number
}

type ProjectionYears = {
  firstYear: ProjectionYear
  lastYear: ProjectionYear
  estimate: Arbitrage
  /**
   * A run that found no bill reduction still shows both columns, but the
   * framing follows the figures the page prints: nothing here calls a bill
   * that did not visibly fall "taking control".
   */
  reducesBill: boolean
}

/**
 * The two years the projection compares, or null when the saved run cannot
 * support a comparison. Two distinct years or nothing: a one-year projection
 * would otherwise print the same figure under both labels as if the bill had
 * been projected forward.
 *
 * Exported so the section hosting the projection can decide whether it has
 * anything at all to show without repeating the rule.
 */
export const billProjectionYears = (
  arbitrage: Arbitrage | null,
): ProjectionYears | null => {
  const firstYear = arbitrage?.projections.at(0) ?? null
  const lastYear = arbitrage?.projections.at(-1) ?? null
  if (
    !arbitrage ||
    !firstYear ||
    !lastYear ||
    lastYear.year <= firstYear.year ||
    firstYear.doNothingUsd == null ||
    firstYear.withBatteryUsd == null ||
    lastYear.doNothingUsd == null ||
    lastYear.withBatteryUsd == null
  ) {
    return null
  }

  return {
    firstYear: {
      ...firstYear,
      doNothingUsd: firstYear.doNothingUsd,
      withBatteryUsd: firstYear.withBatteryUsd,
    },
    lastYear: {
      ...lastYear,
      doNothingUsd: lastYear.doNothingUsd,
      withBatteryUsd: lastYear.withBatteryUsd,
    },
    estimate: arbitrage,
    reducesBill: reducesUtilityBill(arbitrage.annualSavingsUsd),
  }
}

function ProjectedBills({
  heading,
  headingTone,
  valueTone,
  years,
  children,
}: {
  heading: string
  headingTone: string
  valueTone: string
  years: Array<[string, number]>
  children?: ReactNode
}): JSX.Element {
  return (
    <>
      <div
        className={`font-display mb-4 text-[length:var(--step-0)] font-semibold ${headingTone}`}
      >
        {heading}
      </div>
      {years.map(([label, value]) => (
        <div
          key={label}
          className="flex items-baseline justify-between gap-3 border-b border-dashed border-bp-border py-3"
        >
          <span className="text-[length:var(--step--1)] text-bp-text-muted">
            {label}
          </span>
          <span
            className={`font-display text-[length:var(--step-1)] font-semibold tracking-[-0.02em] tabular-nums ${valueTone}`}
          >
            {formatUsd(value)}
          </span>
        </div>
      ))}
      {children}
    </>
  )
}

/**
 * What the utility is projected to charge, with and without the battery, in the
 * saved run's own first and last year. Deliberately separate from the project's
 * own money: this projection never reduces Proposal Cost, the amount financed,
 * or any contract amount.
 *
 * Both years are read straight out of the run, escalation and the run's own
 * degradation already applied, so the page cannot project a bill from
 * assumptions the calculation did not use. The seller page, the homeowner link
 * and the brochure all render this one component, which is why they cannot
 * disagree about the projection.
 */
export function BillProjection({
  arbitrage,
}: {
  arbitrage: Arbitrage | null
}): JSX.Element | null {
  const years = billProjectionYears(arbitrage)
  if (!years) {
    return null
  }

  const billLabel = arbitrageBillLabel(years.estimate)
  const lastYearLabel = `${billLabel} in year ${years.lastYear.year}`

  return (
    <div data-testid="battery-bill-projection">
      <SectionHeading eyebrow="Your energy independence investment">
        {years.estimate.estimateBasis === `generalized` ? (
          <>
            Compare the <GradText>modeled scenarios</GradText>.
          </>
        ) : years.reducesBill ? (
          <>
            Do nothing, or <GradText>take control</GradText>.
          </>
        ) : (
          <>
            What the utility bill does <GradText>either way</GradText>.
          </>
        )}
      </SectionHeading>
      <div className="grid gap-3.5 lg:grid-cols-2">
        <Panel className="px-8 py-8">
          <ProjectedBills
            heading="Without the battery"
            headingTone="text-bp-text-muted"
            valueTone="text-bp-danger"
            years={[
              [`${billLabel} this year`, years.firstYear.doNothingUsd],
              [lastYearLabel, years.lastYear.doNothingUsd],
            ]}
          />
        </Panel>
        <div className="rounded-lg border border-bp-accent bg-bp-accent-soft/40 px-8 py-8">
          <ProjectedBills
            heading="With the battery"
            headingTone="text-bp-accent"
            // The accent tint is a claim of its own, so a with-battery bill
            // that did not fall stays in body colour.
            valueTone={years.reducesBill ? `text-bp-accent` : `text-bp-text`}
            years={[
              [`${billLabel} this year`, years.firstYear.withBatteryUsd],
              [lastYearLabel, years.lastYear.withBatteryUsd],
            ]}
          >
            <p className="mt-4 text-[length:var(--step--2)] text-bp-text-muted">
              {arbitrageBasisQualifier(years.estimate)}.{` `}
              {arbitrageScopeExplanation(years.estimate)}
              {` `}
              Rounded to the nearest dollar. This projection does not change the
              project cost or the amount financed.
            </p>
          </ProjectedBills>
        </div>
      </div>
    </div>
  )
}
